import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { formatDuration, mediaUrl } from "@/lib/format";
import { ImageUpload } from "@/components/ImageUpload";
import { useAuth } from "@/context/AuthContext";
import { MessagesButton } from "@/components/MessagesButton";
import { UpcomingCalendar } from "@/components/UpcomingCalendar";
import { PaymentBadges } from "@/components/PaymentBadges";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { CustomersTab } from "@/components/CustomersTab";
import { ProviderReviewsTab } from "@/components/ProviderReviewsTab";
import { SettlementDialog } from "@/components/SettlementDialog";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { DollarSign, CalendarClock, Package, Plus, Trash2, Pencil, Copy, FileText, Crown, Sparkles, ArrowRight, MapPin, KeyRound, ShieldCheck, Star, CheckCircle2, Loader2, Banknote, Heart, Megaphone, Globe, Instagram, Facebook, Twitter, Linkedin, Music2, Send, CreditCard } from "lucide-react";
import { toast } from "sonner";

const DAYS = [["Mon", 0], ["Tue", 1], ["Wed", 2], ["Thu", 3], ["Fri", 4], ["Sat", 5], ["Sun", 6]];
const TIER_LABEL = { free: "Basic", intermediate: "Intermediate", pro: "Pro", founding: "Founding Provider" };
const tierLabel = (t) => TIER_LABEL[t] || t;
const STATUS = {
  pending: "bg-amber-100 text-amber-800",
  confirmed: "bg-green-100 text-green-800",
  in_progress: "bg-indigo-100 text-indigo-800",
  completed: "bg-blue-100 text-blue-800",
  cancelled: "bg-neutral-200 text-neutral-600",
};
const EMPTY = { title: "", description: "", category: "", service_type: "flat", price: 50, duration_hours: 1, image_url: "", days: [0,1,2,3,4], window_start: "09:00", window_end: "17:00", active: true, package_discount_type: "none", package_discount_value: 0, intake_fields: [], is_recurring: false, recurrence: "weekly", slots_per_day: 1, pause_notice_days: 2, location_type: "provider" };

function Stat({ icon: Icon, label, value }) {
  return (
    <div className="bg-white border border-border rounded-sm p-6">
      <div className="flex items-center gap-2 text-xs uppercase tracking-[0.15em] font-bold text-muted-foreground">
        <Icon className="w-4 h-4" /> {label}
      </div>
      <div className="font-display text-3xl font-semibold mt-3">{value}</div>
    </div>
  );
}

function UsageBar({ label, used, limit }) {
  const unlimited = limit === -1;
  const pct = unlimited ? 0 : Math.min(100, Math.round((used / Math.max(limit, 1)) * 100));
  const near = !unlimited && pct >= 80;
  return (
    <div data-testid={`usage-${label.toLowerCase().replace(/\s+/g, "-")}`}>
      <div className="flex items-center justify-between text-sm">
        <span className="text-foreground/80">{label}</span>
        <span className="font-medium tabular-nums">{used}{unlimited ? "" : ` / ${limit}`}{unlimited && <span className="text-muted-foreground font-normal"> · unlimited</span>}</span>
      </div>
      {!unlimited && (
        <div className="mt-2 h-1.5 w-full rounded-full bg-black/10 overflow-hidden">
          <div className={`h-full rounded-full transition-all ${near ? "bg-primary" : "bg-secondary"}`} style={{ width: `${pct}%` }} />
        </div>
      )}
    </div>
  );
}

function CompleteServiceDialog({ booking, onDone }) {
  const [open, setOpen] = useState(false);
  const [photos, setPhotos] = useState(booking.proof?.photos || []);
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);

  const addPhoto = async (url) => {
    if (!url) return;
    try {
      const { data } = await api.post(`/bookings/${booking.id}/proof-photo`, { url });
      setPhotos(data.proof?.photos || [...photos, url]);
      toast.success("Proof photo added");
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const complete = async () => {
    setBusy(true);
    try {
      const send = (extra) => api.post(`/bookings/${booking.id}/complete`, { code: code.trim(), ...extra });
      const done = () => { toast.success("Service marked complete"); setOpen(false); onDone(); };
      const fail = (err) => toast.error(formatApiErrorDetail(err.response?.data?.detail));
      if (navigator.geolocation) {
        navigator.geolocation.getCurrentPosition(
          (pos) => send({ lat: pos.coords.latitude, lng: pos.coords.longitude }).then(done).catch(fail).finally(() => setBusy(false)),
          () => send({}).then(done).catch(fail).finally(() => setBusy(false)),
          { timeout: 8000 }
        );
      } else { await send({}).then(done).catch(fail); setBusy(false); }
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size="sm" className="rounded-sm h-8" data-testid={`open-complete-${booking.id}`}>Complete service</Button>
      </DialogTrigger>
      <DialogContent className="max-w-md rounded-sm max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="font-display text-2xl">Complete service</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <p className="text-sm text-muted-foreground">
            Upload at least one proof photo taken on-site, then enter the 4-digit code your customer shows in their booking.
          </p>
          <div>
            <Label className="text-sm">Proof photos <span className="text-muted-foreground font-normal">({photos.length} added)</span></Label>
            {photos.length > 0 && (
              <div className="flex flex-wrap gap-2 my-2" data-testid={`proof-photos-${booking.id}`}>
                {photos.map((p, i) => (
                  <img key={i} src={mediaUrl(p)} alt="proof" className="w-16 h-16 object-cover rounded-sm border border-border" />
                ))}
              </div>
            )}
            <div className="mt-1.5">
              <ImageUpload value="" onChange={addPhoto} label="" testid={`proof-upload-${booking.id}`} aspect="aspect-video" />
            </div>
          </div>
          <div>
            <Label htmlFor={`code-${booking.id}`} className="text-sm flex items-center gap-1"><KeyRound className="w-4 h-4" />Customer completion code</Label>
            <Input id={`code-${booking.id}`} inputMode="numeric" maxLength={4} value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
              placeholder="4-digit code" className="mt-1.5 rounded-sm tracking-[0.3em] font-display text-lg" data-testid={`code-input-${booking.id}`} />
          </div>
        </div>
        <DialogFooter>
          <Button className="rounded-sm w-full" onClick={complete} disabled={busy || photos.length === 0 || code.length !== 4} data-testid={`submit-complete-${booking.id}`}>
            {busy ? <><Loader2 className="w-4 h-4 mr-1 animate-spin" />Completing…</> : <><CheckCircle2 className="w-4 h-4 mr-1" />Mark completed</>}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function SubscriberDashboard() {
  const { user, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [services, setServices] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [recurring, setRecurring] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editId, setEditId] = useState(null);
  const [cats, setCats] = useState([]);
  const [socials, setSocials] = useState(user?.socials || {});
  const [socialHashtag, setSocialHashtag] = useState(user?.social_hashtag || "");
  const [marketing, setMarketing] = useState(null);
  const [ai, setAi] = useState({ loading: false, result: null });

  const loadAll = () => {
    api.get("/dashboard/stats").then((r) => setStats(r.data));
    api.get("/services/mine").then((r) => setServices(r.data));
    api.get("/bookings/subscriber").then((r) => setBookings(r.data));
    api.get("/categories").then((r) => setCats(r.data));
    api.get("/subscriptions/provider").then((r) => setRecurring(r.data)).catch(() => {});
    api.get("/marketing/overview").then((r) => setMarketing(r.data)).catch(() => {});
  };
  useEffect(() => { loadAll(); }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const toggleDay = (d) => setForm((f) => ({ ...f, days: f.days.includes(d) ? f.days.filter((x) => x !== d) : [...f.days, d] }));
  const addField = () => setForm((f) => (f.intake_fields || []).length >= 5 ? f : ({ ...f, intake_fields: [...(f.intake_fields || []), { id: (crypto.randomUUID ? crypto.randomUUID() : String(Date.now())), prompt: "", type: "short_text", required: false, options: [] }] }));
  const updateField = (i, patch) => setForm((f) => ({ ...f, intake_fields: f.intake_fields.map((fl, idx) => idx === i ? { ...fl, ...patch } : fl) }));
  const removeField = (i) => setForm((f) => ({ ...f, intake_fields: f.intake_fields.filter((_, idx) => idx !== i) }));

  const openCreate = () => { setForm(EMPTY); setEditId(null); setAi({ loading: false, result: null }); setOpen(true); };
  const openEdit = (s) => { setForm({ ...EMPTY, ...s, duration_hours: Math.round(((s.duration_minutes || 60) / 60) * 100) / 100, package_discount_type: s.package_discount_type || "none" }); setEditId(s.id); setAi({ loading: false, result: null }); setOpen(true); };

  const save = async (e) => {
    e.preventDefault();
    if (form.location_type === "customer" && !user?.identity_verified) {
      toast.error("Verify your identity to offer services performed at the customer's location.");
      return;
    }
    const { duration_hours, duration_minutes: _dm, ...rest } = form;
    const payload = {
      ...rest,
      price: Number(form.price),
      duration_minutes: Math.max(15, Math.round((Number(duration_hours) || 1) * 60)),
      package_discount_type: form.package_discount_type === "none" ? null : form.package_discount_type,
      package_discount_value: Number(form.package_discount_value) || 0,
      is_recurring: !!form.is_recurring,
      recurrence: form.is_recurring ? form.recurrence : null,
      slots_per_day: Math.max(1, Number(form.slots_per_day) || 1),
      pause_notice_days: Math.max(0, Number(form.pause_notice_days) || 0),
      intake_fields: (form.intake_fields || []).map(({ optionsText, ...fl }) => fl),
    };
    try {
      if (editId) await api.put(`/services/${editId}`, payload);
      else await api.post("/services", payload);
      toast.success(editId ? "Service updated" : "Service created");
      setOpen(false);
      loadAll();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const del = async (id) => {
    try { await api.delete(`/services/${id}`); toast.success("Deleted"); loadAll(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const clone = async (id) => {
    try { await api.post(`/services/${id}/clone`); toast.success("Service duplicated"); loadAll(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const downloadInvoice = async (id) => {
    try {
      const res = await api.get(`/bookings/${id}/invoice.pdf`, { responseType: "blob" });
      const url = window.URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = `invoice-${id.slice(0, 8)}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) { toast.error("Couldn't download the invoice."); }
  };

  const setStatus = async (id, status) => {
    try { await api.put(`/bookings/${id}/status`, { status }); toast.success(`Marked ${status}`); loadAll(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const cancelRecurring = async (id) => {
    const reason = window.prompt("Cancel this recurring subscription? Optionally tell the customer why (e.g. non-payment). Leave blank for no reason:", "");
    if (reason === null) return;
    try { await api.post(`/subscriptions/${id}/cancel`, { reason: reason.trim() }); toast.success("Subscription cancelled"); loadAll(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const declineBooking = async (id) => {
    const reason = window.prompt("Optionally tell the customer why you're declining (they'll be notified). Leave blank to decline without a reason:", "");
    if (reason === null) return;
    try { await api.put(`/bookings/${id}/status`, { status: "cancelled", reason: reason.trim() }); toast.success("Booking declined"); loadAll(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const checkin = async (id) => {
    const send = (body) => api.post(`/bookings/${id}/checkin`, body)
      .then(() => { toast.success("Checked in — service started"); loadAll(); })
      .catch((err) => toast.error(formatApiErrorDetail(err.response?.data?.detail)));
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (pos) => send({ lat: pos.coords.latitude, lng: pos.coords.longitude }),
        () => send({}),
        { timeout: 8000 }
      );
    } else { send({}); }
  };

  const confirmPayment = async (id) => {
    try {
      await api.post(`/bookings/${id}/confirm-payment`);
      toast.success("Payment confirmed — the customer's receipt is emailed once the job is completed");
      loadAll();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const [charging, setCharging] = useState(null);
  const openBillingPortal = async () => {
    try {
      const { data } = await api.post("/subscription/billing-portal", { origin_url: window.location.origin });
      if (data.url) window.location.href = data.url;
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Couldn't open the billing portal. Please try again shortly.");
    }
  };
  const chargeBalance = async (id) => {
    setCharging(id);
    try {
      await api.post(`/bookings/${id}/charge-balance`);
      toast.success("Balance charged to the customer's saved card");
      loadAll();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setCharging(null); }
  };

  const saveMarketing = async () => {
    try {
      const { data } = await api.put("/profile", { socials, social_hashtag: socialHashtag });
      refreshUser(data.user);
      toast.success("Marketing settings saved");
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const generateAI = async () => {
    if (!(form.title.trim().length >= 3 && form.category && form.description.trim().length >= 15)) {
      toast.error("Add a title, category, and a short description first.");
      return;
    }
    setAi({ loading: true, result: null });
    try {
      const { data } = await api.post("/services/ai-draft", {
        category: form.category, title: form.title, description: form.description,
        price: Number(form.price) || null, service_type: form.service_type,
      });
      set("title", data.title || form.title);
      set("description", data.description || form.description);
      setAi({ loading: false, result: data });
      toast.success("AI draft ready — title & description filled in");
    } catch (err) {
      setAi({ loading: false, result: null });
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  if (!stats) return <div className="py-20 text-center text-muted-foreground">Loading…</div>;
  const limit = stats.limits.max_services;
  const aiReady = form.title.trim().length >= 3 && !!form.category && form.description.trim().length >= 15;

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-10">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="font-display text-4xl font-semibold tracking-tighter">Dashboard</h1>
          <p className="text-muted-foreground mt-1">Welcome back, {user.name}.</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <MessagesButton />
          <Link to="/provider/packages">
            <Button variant="outline" className="rounded-sm" data-testid="manage-packages-link">
              <Package className="w-4 h-4 mr-1.5" />Manage packages
            </Button>
          </Link>
          {stats.tier === "founding" ? (
            <div className="flex items-center gap-2 border border-amber-300 rounded-sm px-4 py-2 bg-amber-50" data-testid="tier-badge">
              <Sparkles className="w-4 h-4 text-amber-600" />
              <span className="text-sm font-medium text-amber-800">Founding Provider</span>
              <span className="text-[10px] uppercase tracking-wider font-bold text-amber-700 bg-amber-200/60 rounded-sm px-1.5 py-0.5">
                {user?.founding_expires_at ? `until ${new Date(user.founding_expires_at).toLocaleDateString()}` : "Lifetime · $0/mo"}
              </span>
            </div>
          ) : (
            <Link to="/pricing">
              <div className="flex items-center gap-2 border border-border rounded-sm px-4 py-2 bg-white hover:border-secondary transition-all" data-testid="tier-badge">
                <Crown className="w-4 h-4 text-primary" />
                <span className="text-sm font-medium">{tierLabel(stats.tier)} plan</span>
              </div>
            </Link>
          )}
        </div>
      </div>

      {user?.subscription_past_due && (
        <div className="mt-6 rounded-sm border border-red-300 bg-red-50 p-4 flex flex-wrap items-center justify-between gap-3" data-testid="past-due-banner">
          <div className="flex items-start gap-2.5">
            <Banknote className="w-5 h-5 text-red-600 shrink-0 mt-0.5" />
            <div>
              <p className="font-medium text-red-900">Payment failed — update your card</p>
              <p className="text-sm text-red-800">Your subscription payment didn't go through. Update your payment method to keep your paid features — you'll lose them if it isn't resolved.</p>
            </div>
          </div>
          <Button className="rounded-sm shrink-0" onClick={openBillingPortal} data-testid="update-payment-btn">Update payment</Button>
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-8">
        <Stat icon={DollarSign} label="Revenue" value={`$${stats.revenue}`} />
        <Stat icon={Package} label="Services" value={`${stats.active_services}${limit === -1 ? "" : `/${limit}`}`} />
        <Stat icon={CalendarClock} label="Pending" value={stats.pending} />
      </div>

      <UpcomingCalendar
        bookings={bookings}
        onConfirm={(id) => setStatus(id, "confirmed")}
        onCheckin={checkin}
        onMessage={(b) => navigate(`/messages?to=${b.customer_id}`)}
      />

      {/* Upsell — shown while on the Basic (free-trial) plan */}
      {stats.tier === "free" && (
        <div className="mt-6 rounded-sm border border-primary/30 bg-primary/[0.04] p-6" data-testid="upgrade-prompt">
          <div className="flex flex-col lg:flex-row lg:items-center gap-6">
            <div className="lg:w-1/2">
              <div className="flex items-center gap-2 text-primary">
                <Sparkles className="w-4 h-4" />
                <span className="text-xs uppercase tracking-[0.2em] font-bold">You're on Basic</span>
              </div>
              <h2 className="font-display text-2xl font-semibold tracking-tight mt-2">Pick your plan</h2>
              <p className="text-sm text-muted-foreground mt-1">
                Unlock more services, higher monthly booking limits, analytics and branding. Upgrade whenever you're ready — cancel anytime.
              </p>
              <Link to="/pricing" className="inline-block mt-4">
                <Button className="rounded-sm" data-testid="upgrade-prompt-cta">
                  Pick your plan <ArrowRight className="w-4 h-4 ml-1.5" />
                </Button>
              </Link>
            </div>
            <div className="lg:w-1/2 space-y-4 lg:pl-8 lg:border-l border-primary/20">
              <p className="text-xs uppercase tracking-[0.15em] font-bold text-muted-foreground">Your Basic usage this month</p>
              <UsageBar label="Services" used={stats.active_services} limit={stats.limits.max_services} />
              <UsageBar label="Bookings" used={stats.month_bookings} limit={stats.limits.max_bookings_per_month} />
            </div>
          </div>
        </div>
      )}

      <Tabs defaultValue="services" className="mt-10">
        <TabsList className="rounded-sm flex flex-wrap h-auto">
          <TabsTrigger value="services" data-testid="tab-services" className="rounded-sm">Services</TabsTrigger>
          <TabsTrigger value="bookings" data-testid="tab-bookings" className="rounded-sm">Bookings</TabsTrigger>
          <TabsTrigger value="customers" data-testid="tab-customers" className="rounded-sm">Customers</TabsTrigger>
          <TabsTrigger value="reviews" data-testid="tab-reviews" className="rounded-sm">Reviews</TabsTrigger>
          <TabsTrigger value="recurring" data-testid="tab-recurring" className="rounded-sm">Recurring{recurring.filter((s) => s.status === "active").length > 0 && <span className="ml-1.5 text-xs bg-primary text-white rounded-sm px-1.5">{recurring.filter((s) => s.status === "active").length}</span>}</TabsTrigger>
          <TabsTrigger value="marketing" data-testid="tab-marketing" className="rounded-sm">Marketing</TabsTrigger>
        </TabsList>

        {/* Bookings */}
        <TabsContent value="bookings" className="mt-6">
          {bookings.length === 0 ? (
            <p className="text-muted-foreground" data-testid="sub-bookings-empty">No bookings yet.</p>
          ) : (
            <div className="bg-white border border-border rounded-sm overflow-x-auto">
              <table className="w-full text-sm min-w-[640px]">
                <thead className="bg-muted text-left text-xs uppercase tracking-wider text-muted-foreground">
                  <tr><th className="p-4">Customer</th><th className="p-4">Service</th><th className="p-4">When</th><th className="p-4">Status</th><th className="p-4 text-right">Actions</th></tr>
                </thead>
                <tbody>
                  {bookings.map((b) => (
                    <tr key={b.id} className="border-t border-border" data-testid={`sub-booking-${b.id}`}>
                      <td className="p-4 font-medium">{b.customer_name}</td>
                      <td className="p-4">
                        <div>{b.service_title}</div>
                        {(b.notes || (b.intake_answers && Object.keys(b.intake_answers).length > 0)) && (
                          <div className="mt-1.5 text-xs text-muted-foreground space-y-0.5 max-w-xs" data-testid={`booking-intake-${b.id}`}>
                            {(b.intake_fields || []).map((fl) => {
                              const ans = b.intake_answers?.[fl.id];
                              if (ans === undefined || ans === "" || ans === null) return null;
                              return <div key={fl.id}><span className="font-medium text-foreground">{fl.prompt}:</span> {String(ans)}</div>;
                            })}
                            {b.notes && <div><span className="font-medium text-foreground">Note:</span> {b.notes}</div>}
                          </div>
                        )}
                      </td>
                      <td className="p-4 text-muted-foreground">{b.date} · {b.start_time}</td>
                      <td className="p-4"><span className={`text-xs px-2 py-0.5 rounded-sm font-medium ${STATUS[b.status]}`}>{b.status}</span></td>
                      <td className="p-4">
                        <div className="flex gap-2 justify-end items-center">
                          {b.status === "pending" && <Button size="sm" className="rounded-sm h-8" onClick={() => setStatus(b.id, "confirmed")} data-testid={`confirm-${b.id}`}>Confirm</Button>}
                          {b.status === "confirmed" && <Button size="sm" className="rounded-sm h-8" onClick={() => checkin(b.id)} data-testid={`checkin-${b.id}`}><MapPin className="w-3.5 h-3.5 mr-1" />Check in</Button>}
                          {b.status === "in_progress" && <CompleteServiceDialog booking={b} onDone={loadAll} />}
                          {b.status === "completed" && (
                            b.proof?.customer_confirmed
                              ? <span className="text-xs text-blue-700 flex items-center gap-1" data-testid={`verified-${b.id}`}><ShieldCheck className="w-4 h-4" />verified{b.proof?.rating ? <span className="flex items-center gap-0.5 ml-1">{b.proof.rating}<Star className="w-3.5 h-3.5 fill-amber-400 text-amber-400" /></span> : null}</span>
                              : <span className="text-xs text-muted-foreground" data-testid={`awaiting-confirm-${b.id}`}>Awaiting customer confirmation</span>
                          )}
                          {["pending","confirmed"].includes(b.status) && <Button size="sm" variant="outline" className="rounded-sm h-8" onClick={() => declineBooking(b.id)} data-testid={`decline-${b.id}`}>Decline</Button>}
                          {b.service_type === "service_call" && b.payment_status === "paid" && !["paid","direct"].includes(b.settlement?.status) && <SettlementDialog booking={b} onDone={loadAll} />}
                          {b.payment_model === "deposit" && ["in_progress","completed"].includes(b.status) && !b.balance_paid_confirmed && b.balance_card_saved && user?.stripe_payouts_enabled && (b.service_type !== "service_call" || b.settlement?.status === "direct") && (
                            <Button size="sm" className="rounded-sm h-8" disabled={charging === b.id} onClick={() => chargeBalance(b.id)} data-testid={`charge-balance-${b.id}`}><CreditCard className="w-3.5 h-3.5 mr-1" />{charging === b.id ? "Charging…" : `Charge remaining balance ($${Number(b.balance_due||0).toFixed(2)})`}</Button>
                          )}
                          {b.payment_model === "deposit" && ["in_progress","completed"].includes(b.status) && !b.balance_paid_confirmed && (b.service_type !== "service_call" || b.settlement?.status === "direct") && (
                            <Button size="sm" variant={b.balance_card_saved && user?.stripe_payouts_enabled ? "outline" : "default"} className="rounded-sm h-8" onClick={() => confirmPayment(b.id)} data-testid={`confirm-payment-${b.id}`}>Payment received (${Number(b.balance_due||0).toFixed(2)})</Button>
                          )}
                          {b.payment_model === "deposit" && b.balance_paid_confirmed && (
                            <span className="text-xs text-green-700 flex items-center gap-1" data-testid={`payment-confirmed-${b.id}`}><CheckCircle2 className="w-3.5 h-3.5" />Payment confirmed</span>
                          )}
                          {(b.payment_model === "deposit" ? b.receipt_sent : b.payment_status === "paid") && <Button size="sm" variant="outline" className="rounded-sm h-8" onClick={() => downloadInvoice(b.id)} data-testid={`invoice-${b.id}`}><FileText className="w-3.5 h-3.5 mr-1" />{b.payment_model === "deposit" ? "Receipt" : "Invoice"}</Button>}
                          {b.service_type === "service_call" && b.settlement?.status === "invoiced" && <span className="text-xs text-amber-700 whitespace-nowrap" data-testid={`settlement-awaiting-${b.id}`}>Balance ${Number(b.settlement.balance_due).toFixed(2)} due</span>}
                          {b.service_type === "service_call" && b.settlement?.status === "direct" && <span className="text-xs text-amber-700 whitespace-nowrap" data-testid={`settlement-direct-${b.id}`}>Collect ${Number(b.settlement.balance_due).toFixed(2)} directly</span>}
                          {b.service_type === "service_call" && b.settlement?.status === "paid" && <span className="text-xs text-green-700 whitespace-nowrap" data-testid={`settlement-paid-${b.id}`}>Settled ${Number(b.settlement.final_total).toFixed(2)}</span>}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </TabsContent>

        {/* Services */}
        <TabsContent value="services" className="mt-6">
          <div className="flex justify-between items-center mb-4">
            <p className="text-sm text-muted-foreground">{services.length} service(s){limit !== -1 && ` · ${limit} allowed on ${tierLabel(stats.tier)}`}</p>
            <Dialog open={open} onOpenChange={setOpen}>
              <DialogTrigger asChild>
                <Button className="rounded-sm" onClick={openCreate} data-testid="add-service-btn"><Plus className="w-4 h-4 mr-1" />New service</Button>
              </DialogTrigger>
              <DialogContent className="max-w-lg rounded-sm max-h-[90vh] overflow-y-auto">
                <DialogHeader><DialogTitle className="font-display text-2xl">{editId ? "Edit" : "New"} service</DialogTitle><DialogDescription className="sr-only">Create or edit a service listing.</DialogDescription></DialogHeader>
                <form onSubmit={save} className="space-y-4">
                  {["intermediate", "pro", "founding"].includes(stats.tier) && (
                    <div className="rounded-sm border border-primary/30 bg-primary/5 p-3" data-testid="ai-assistant">
                      <div className="flex items-center justify-between gap-2">
                        <div className="flex items-center gap-1.5 text-sm font-medium"><Sparkles className="w-4 h-4 text-primary" />AI Marketing Assistant</div>
                        <Button type="button" size="sm" className="rounded-sm h-8" onClick={generateAI} disabled={ai.loading || !aiReady} data-testid="ai-generate-btn">
                          {ai.loading ? <><Loader2 className="w-4 h-4 mr-1.5 animate-spin" />Generating…</> : <><Sparkles className="w-4 h-4 mr-1.5" />Generate with AI</>}
                        </Button>
                      </div>
                      <p className="text-xs text-muted-foreground mt-1" data-testid="ai-hint">
                        {aiReady
                          ? <>Polishes your title &amp; description and drafts a social post{["pro", "founding"].includes(stats.tier) ? " — plus bundle-partner ideas" : ""}.</>
                          : <>Add a <b>title</b>, pick a <b>category</b>, and write a short <b>description</b> (15+ chars) first so the AI has enough context to write accurate copy.</>}
                      </p>
                      {ai.result && (
                        <div className="mt-3 space-y-3">
                          {ai.result.social_post && (
                            <div data-testid="ai-social-post">
                              <div className="flex items-center justify-between"><Label className="text-xs">Social media post</Label>
                                <Button type="button" size="sm" variant="ghost" className="h-7 text-xs" onClick={() => { navigator.clipboard.writeText(ai.result.social_post); toast.success("Copied!"); }} data-testid="ai-copy-social">Copy</Button>
                              </div>
                              <p className="text-sm bg-white border border-border rounded-sm p-2 mt-1 whitespace-pre-wrap">{ai.result.social_post}</p>
                            </div>
                          )}
                          {ai.result.bundle_partners?.length > 0 && (
                            <div data-testid="ai-bundle-partners">
                              <Label className="text-xs">Suggested bundle partners</Label>
                              <div className="flex flex-wrap gap-1.5 mt-1">
                                {ai.result.bundle_partners.map((b, i) => (
                                  <span key={i} className="text-xs bg-white border border-border rounded-sm px-2 py-1" title={b.why} data-testid={`ai-bundle-${i}`}>{b.type}</span>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                  <div><Label>Title</Label><Input value={form.title} onChange={(e) => set("title", e.target.value)} required className="mt-1 rounded-sm" data-testid="service-title" /></div>
                  <div><Label>Description</Label><Textarea value={form.description} onChange={(e) => set("description", e.target.value)} className="mt-1 rounded-sm" data-testid="service-desc" /></div>
                  <div className="grid grid-cols-2 gap-3">
                    <div><Label>Category</Label>
                      <Select value={form.category} onValueChange={(v) => set("category", v)}>
                        <SelectTrigger className="mt-1 rounded-sm" data-testid="service-category"><SelectValue placeholder="Select" /></SelectTrigger>
                        <SelectContent>{cats.map((c) => <SelectItem key={c.id} value={c.name}>{c.name}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                    <div><ImageUpload value={form.image_url} onChange={(u) => set("image_url", u)} label="Service image" testid="service-image" /></div>
                  </div>
                  <div>
                    <Label>Service type</Label>
                    <Select value={form.service_type || "flat"} onValueChange={(v) => set("service_type", v)}>
                      <SelectTrigger className="mt-1 rounded-sm" data-testid="service-type"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="flat">Flat price</SelectItem>
                        <SelectItem value="service_call">Service call (base fee + on-site parts &amp; labor)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div><Label>{form.service_type === "service_call" ? "Base price ($)" : "Price ($)"}</Label><Input type="number" min="0" value={form.price} onChange={(e) => set("price", e.target.value)} className="mt-1 rounded-sm" data-testid="service-price" /></div>
                    <div><Label>Duration (hours)</Label><Input type="number" min="0.25" step="0.25" value={form.duration_hours} onChange={(e) => set("duration_hours", e.target.value)} className="mt-1 rounded-sm" data-testid="service-duration" /></div>
                  </div>
                  {form.service_type === "service_call" && (
                    <p className="text-xs text-muted-foreground -mt-1 rounded-sm bg-muted/50 border border-border p-2.5" data-testid="service-call-hint">
                      The <b>base price</b> is the minimum charged and is collected when the customer books. Once on-site, you can assess additional parts &amp; labor; the base fee is credited toward the final total.
                    </p>
                  )}
                  <div>
                    <Label>Available days</Label>
                    <div className="flex flex-wrap gap-2 mt-2">
                      {DAYS.map(([lbl, d]) => (
                        <button type="button" key={d} onClick={() => toggleDay(d)} data-testid={`day-${d}`}
                          className={`px-3 py-1.5 border rounded-sm text-sm ${form.days.includes(d) ? "bg-secondary text-white border-secondary" : "border-border"}`}>{lbl}</button>
                      ))}
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div><Label>Opens</Label><Input type="time" value={form.window_start} onChange={(e) => set("window_start", e.target.value)} className="mt-1 rounded-sm" data-testid="service-start" /></div>
                    <div><Label>Closes</Label><Input type="time" value={form.window_end} onChange={(e) => set("window_end", e.target.value)} className="mt-1 rounded-sm" data-testid="service-end" /></div>
                  </div>
                  <div className="flex items-center gap-2"><Switch checked={form.active} onCheckedChange={(v) => set("active", v)} data-testid="service-active" /><Label>Active (visible to customers)</Label></div>
                  <div>
                    <Label>Where is this service performed?</Label>
                    <Select value={form.location_type || "provider"} onValueChange={(v) => set("location_type", v)}>
                      <SelectTrigger className="mt-1 rounded-sm" data-testid="service-location-type"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="provider">At my business address</SelectItem>
                        <SelectItem value="customer">At the customer's location</SelectItem>
                      </SelectContent>
                    </Select>
                    {form.location_type === "customer" && !user?.identity_verified && (
                      <div className="mt-2 rounded-sm border border-amber-300 bg-amber-50 p-3 text-xs text-amber-900" data-testid="idv-required-warning">
                        <b>Identity verification required.</b> To list a service performed at the customer's location, verify your identity first (Identity verification section on your dashboard). This keeps customers safe and unlocks your green Verified shield.
                      </div>
                    )}
                  </div>
                  <div className="rounded-sm border border-border bg-muted/40 p-3">
                    <div className="flex items-center gap-2">
                      <Switch checked={form.is_recurring} onCheckedChange={(v) => set("is_recurring", v)} data-testid="service-recurring" />
                      <Label className="text-sm">Recurring service <span className="text-muted-foreground font-normal">(ongoing, e.g. weekly pool cleaning)</span></Label>
                    </div>
                    {form.is_recurring && (
                      <div className="mt-3 space-y-3">
                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            <Label className="text-sm">Repeats</Label>
                            <Select value={form.recurrence} onValueChange={(v) => set("recurrence", v)}>
                              <SelectTrigger className="mt-1 rounded-sm" data-testid="service-recurrence"><SelectValue /></SelectTrigger>
                              <SelectContent>
                                <SelectItem value="weekly">Weekly</SelectItem>
                                <SelectItem value="monthly">Monthly</SelectItem>
                                <SelectItem value="quarterly">Quarterly</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                          <div>
                            <Label className="text-sm">Slots per day</Label>
                            <Input type="number" min="1" value={form.slots_per_day} onChange={(e) => set("slots_per_day", e.target.value)} className="mt-1 rounded-sm" data-testid="service-slots-per-day" />
                          </div>
                        </div>
                        <div>
                          <Label className="text-sm">Pause notice (days ahead)</Label>
                          <Input type="number" min="0" value={form.pause_notice_days} onChange={(e) => set("pause_notice_days", e.target.value)} className="mt-1 rounded-sm" data-testid="service-pause-notice" />
                          <p className="text-xs text-muted-foreground mt-1">Customers must pause an upcoming visit at least this many days in advance.</p>
                        </div>
                        <p className="text-xs text-muted-foreground">This recurring policy will be shown clearly on the service page for customers.</p>
                      </div>
                    )}
                  </div>
                  <div className="rounded-sm border border-border bg-muted/40 p-3">
                    <Label className="text-sm">Package discount <span className="text-muted-foreground font-normal">(applied when this service is bundled)</span></Label>
                    <div className="grid grid-cols-2 gap-3 mt-2">
                      <Select value={form.package_discount_type} onValueChange={(v) => set("package_discount_type", v)}>
                        <SelectTrigger className="rounded-sm" data-testid="service-discount-type"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">No discount</SelectItem>
                          <SelectItem value="percent">Percent (%)</SelectItem>
                          <SelectItem value="flat">Flat ($)</SelectItem>
                        </SelectContent>
                      </Select>
                      <Input type="number" min="0" step="0.01" value={form.package_discount_value}
                        onChange={(e) => set("package_discount_value", e.target.value)} disabled={form.package_discount_type === "none"}
                        placeholder="Amount" className="rounded-sm" data-testid="service-discount-value" />
                    </div>
                  </div>
                  <div className="rounded-sm border border-border bg-muted/40 p-3">
                    <div className="flex items-center justify-between">
                      <Label className="text-sm">Intake questions <span className="text-muted-foreground font-normal">(asked when a customer books, max 5)</span></Label>
                      <Button type="button" size="sm" variant="outline" className="rounded-sm h-8" onClick={addField} disabled={(form.intake_fields || []).length >= 5} data-testid="add-intake-field">
                        <Plus className="w-3.5 h-3.5 mr-1" />Add question
                      </Button>
                    </div>
                    <div className="space-y-3 mt-3">
                      {(form.intake_fields || []).map((fl, i) => (
                        <div key={fl.id} className="rounded-sm border border-border bg-white p-3 space-y-2" data-testid={`intake-field-${i}`}>
                          <div className="flex gap-2">
                            <Input value={fl.prompt} onChange={(e) => updateField(i, { prompt: e.target.value })} placeholder="Question (e.g. How many bedrooms?)" className="rounded-sm" data-testid={`intake-prompt-${i}`} />
                            <Button type="button" size="sm" variant="ghost" className="rounded-sm h-9 px-2 shrink-0" onClick={() => removeField(i)} data-testid={`remove-intake-${i}`}><Trash2 className="w-4 h-4" /></Button>
                          </div>
                          <div className="grid grid-cols-2 gap-2">
                            <Select value={fl.type} onValueChange={(v) => updateField(i, { type: v })}>
                              <SelectTrigger className="rounded-sm h-9" data-testid={`intake-type-${i}`}><SelectValue /></SelectTrigger>
                              <SelectContent>
                                <SelectItem value="short_text">Short text</SelectItem>
                                <SelectItem value="long_text">Long text</SelectItem>
                                <SelectItem value="number">Number</SelectItem>
                                <SelectItem value="yes_no">Yes / No</SelectItem>
                                <SelectItem value="dropdown">Dropdown</SelectItem>
                              </SelectContent>
                            </Select>
                            <div className="flex items-center gap-2 justify-end">
                              <Switch checked={fl.required} onCheckedChange={(v) => updateField(i, { required: v })} data-testid={`intake-required-${i}`} />
                              <Label className="text-sm">Required</Label>
                            </div>
                          </div>
                          {fl.type === "dropdown" && (
                            <Input value={fl.optionsText !== undefined ? fl.optionsText : (fl.options || []).join(", ")} onChange={(e) => updateField(i, { optionsText: e.target.value, options: e.target.value.split(",").map((o) => o.trim()).filter(Boolean) })} placeholder="Options, comma-separated (e.g. Small, Medium, Large)" className="rounded-sm" data-testid={`intake-options-${i}`} />
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                  <DialogFooter><Button type="submit" className="rounded-sm w-full" data-testid="save-service-btn">{editId ? "Save changes" : "Create service"}</Button></DialogFooter>
                </form>
              </DialogContent>
            </Dialog>
          </div>

          {services.length === 0 ? (
            <div className="border border-dashed border-border rounded-sm p-12 text-center" data-testid="services-empty">
              <p className="text-muted-foreground">No services yet. Create your first one.</p>
            </div>
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {services.map((s) => (
                <div key={s.id} className="bg-white border border-border rounded-sm p-5" data-testid={`my-service-${s.id}`}>
                  <div className="flex justify-between items-start">
                    <div>
                      <h3 className="font-display text-lg font-medium">{s.title}</h3>
                      <p className="text-xs text-muted-foreground mt-0.5">
                        {s.category} · {formatDuration(s.duration_minutes)} · {s.service_type === "service_call" ? `from $${s.price} base` : `$${s.price}`}
                      </p>
                      {s.service_type === "service_call" && <span className="inline-block mt-1 text-[10px] uppercase tracking-wider font-bold text-primary bg-primary/10 rounded-sm px-1.5 py-0.5" data-testid={`service-call-badge-${s.id}`}>Service call</span>}
                      <PaymentBadges methods={user?.payment_methods} className="mt-1.5" />
                      {s.like_count > 0 && <p className="text-xs text-primary mt-1 flex items-center gap-1" data-testid={`likes-${s.id}`}><Heart className="w-3 h-3 fill-primary" />{s.like_count} {s.like_count === 1 ? "like" : "likes"}</p>}
                    </div>
                    <span className={`text-xs px-2 py-0.5 rounded-sm ${s.active ? "bg-green-100 text-green-800" : "bg-neutral-200 text-neutral-600"}`}>{s.active ? "active" : "hidden"}</span>
                  </div>
                  <div className="flex gap-2 mt-4">
                    <Button size="sm" variant="outline" className="rounded-sm flex-1" onClick={() => openEdit(s)} data-testid={`edit-${s.id}`}><Pencil className="w-3.5 h-3.5 mr-1" />Edit</Button>
                    <Button size="sm" variant="outline" className="rounded-sm" onClick={() => clone(s.id)} data-testid={`duplicate-${s.id}`}><Copy className="w-3.5 h-3.5 mr-1" />Duplicate</Button>
                    <Button size="sm" variant="outline" className="rounded-sm" onClick={() => del(s.id)} data-testid={`delete-${s.id}`}><Trash2 className="w-3.5 h-3.5" /></Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="customers" className="mt-6">
          <CustomersTab />
        </TabsContent>

        <TabsContent value="reviews" className="mt-6">
          <ProviderReviewsTab />
        </TabsContent>


        {/* Recurring subscribers */}
        <TabsContent value="recurring" className="mt-6">
          {recurring.length === 0 ? (
            <div className="border border-dashed border-border rounded-sm p-12 text-center" data-testid="recurring-empty">
              <p className="text-muted-foreground">No recurring subscribers yet. Enable "Recurring service" on a service to start.</p>
            </div>
          ) : (
            <div className="space-y-4">
              {recurring.map((s) => (
                <div key={s.id} className="bg-white border border-border rounded-sm p-5" data-testid={`recurring-${s.id}`}>
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="text-[10px] uppercase tracking-wider font-bold text-primary bg-primary/10 rounded-sm px-1.5 py-0.5 capitalize">{s.frequency}</span>
                        <span className={`text-[10px] uppercase tracking-wider font-bold rounded-sm px-1.5 py-0.5 ${s.status === "active" ? "bg-green-100 text-green-800" : "bg-red-100 text-red-700"}`}>{s.status}</span>
                      </div>
                      <h3 className="font-display text-lg font-medium mt-2">{s.service_title}</h3>
                      <p className="text-sm text-muted-foreground mt-0.5">{s.customer_name} · {s.start_time} · ${s.price}/visit</p>
                    </div>
                    {s.status === "active" && (
                      <Button size="sm" variant="outline" className="rounded-sm h-8 text-destructive border-destructive/40 hover:bg-destructive/5" onClick={() => cancelRecurring(s.id)} data-testid={`cancel-recurring-${s.id}`}>Cancel</Button>
                    )}
                  </div>
                  {s.status === "active" && (s.occurrences || []).length > 0 && (
                    <div className="mt-3 flex flex-wrap gap-2">
                      {s.occurrences.map((o) => (
                        <span key={o.date} className={`text-xs px-2 py-1 rounded-sm border ${o.status === "paid" ? "border-green-300 bg-green-50 text-green-800" : o.status === "paused" ? "border-amber-300 bg-amber-50 text-amber-800 line-through" : o.status === "due" ? "border-primary/40 bg-primary/5 text-primary" : "border-border text-muted-foreground"}`} data-testid={`prov-occ-${s.id}-${o.date}`}>
                          {new Date(o.date + "T00:00:00").toLocaleDateString("en", { month: "short", day: "numeric" })}{o.status === "paid" ? " ✓" : o.status === "paused" ? " ⏸" : o.status === "due" ? " • due" : ""}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </TabsContent>

        {/* Badges */}
        <TabsContent value="marketing" className="mt-6">
          <div className="grid lg:grid-cols-2 gap-8">
            <div className="space-y-6">
              <div>
                <h3 className="font-display text-lg font-semibold flex items-center gap-2"><Megaphone className="w-5 h-5 text-primary" />Social &amp; website links</h3>
                <p className="text-sm text-muted-foreground mt-0.5">Shown on your public profile. Clear a field to remove it.</p>
                <div className="grid sm:grid-cols-2 gap-3 mt-3">
                  {[
                    { k: "website", ph: "yoursite.com", Icon: Globe },
                    { k: "instagram", ph: "instagram.com/you", Icon: Instagram },
                    { k: "facebook", ph: "facebook.com/you", Icon: Facebook },
                    { k: "x", ph: "x.com/you", Icon: Twitter },
                    { k: "linkedin", ph: "linkedin.com/in/you", Icon: Linkedin },
                    { k: "tiktok", ph: "tiktok.com/@you", Icon: Music2 },
                  ].map(({ k, ph, Icon }) => (
                    <div key={k} className="flex items-center">
                      <span className="inline-flex items-center justify-center w-10 h-10 rounded-l-sm border border-r-0 border-input bg-muted text-muted-foreground shrink-0"><Icon className="w-4 h-4" /></span>
                      <Input value={socials[k] || ""} onChange={(e) => setSocials((s) => ({ ...s, [k]: e.target.value }))}
                        placeholder={ph} className="rounded-l-none rounded-r-sm" data-testid={`social-${k}-input`} />
                    </div>
                  ))}
                </div>
              </div>
              <div>
                <Label htmlFor="social-hashtag">Social hashtag <span className="text-muted-foreground font-normal">(optional)</span></Label>
                <div className="flex items-center mt-1.5">
                  <span className="inline-flex items-center px-3 h-10 rounded-l-sm border border-r-0 border-input bg-muted text-muted-foreground">#</span>
                  <Input id="social-hashtag" value={socialHashtag} onChange={(e) => setSocialHashtag(e.target.value)}
                    placeholder="yourbusiness" className="rounded-l-none rounded-r-sm" data-testid="social-hashtag-input" />
                </div>
                <p className="text-xs text-muted-foreground mt-1.5" data-testid="social-hashtag-disclaimer">
                  By adding a hashtag, you allow BookdHUB to include it in social media posts we create about you (for example, when you reach a Top 50 or Top 100 milestone). Leave blank to opt out.
                </p>
              </div>
              <Button onClick={saveMarketing} className="rounded-sm" data-testid="save-marketing-btn">Save links &amp; hashtag</Button>
            </div>

            <div className="space-y-6">
              <div className="grid grid-cols-3 gap-3">
                <div className="bg-white border border-border rounded-sm p-4" data-testid="mkt-stat-likes">
                  <div className="flex items-center gap-1.5 text-muted-foreground text-xs uppercase tracking-wide"><Heart className="w-3.5 h-3.5" />Likes</div>
                  <div className="font-display text-2xl font-semibold mt-1">{marketing?.total_likes ?? 0}</div>
                </div>
                <div className="bg-white border border-border rounded-sm p-4" data-testid="mkt-stat-pkg-active">
                  <div className="flex items-center gap-1.5 text-muted-foreground text-xs uppercase tracking-wide"><Package className="w-3.5 h-3.5" />Packages</div>
                  <div className="font-display text-2xl font-semibold mt-1">{marketing?.packages?.active ?? 0}</div>
                </div>
                <div className="bg-white border border-border rounded-sm p-4" data-testid="mkt-stat-pkg-pending">
                  <div className="text-muted-foreground text-xs uppercase tracking-wide">Pending</div>
                  <div className="font-display text-2xl font-semibold mt-1">{marketing?.packages?.pending ?? 0}</div>
                </div>
              </div>

              {marketing?.top_services?.length > 0 && (
                <div>
                  <h4 className="text-sm font-semibold mb-2">Most-liked services</h4>
                  <div className="space-y-1.5">
                    {marketing.top_services.map((s, i) => (
                      <div key={i} className="flex items-center justify-between text-sm bg-white border border-border rounded-sm px-3 py-2" data-testid={`mkt-top-service-${i}`}>
                        <span className="truncate">{s.title}</span>
                        <span className="flex items-center gap-1 text-muted-foreground shrink-0"><Heart className="w-3.5 h-3.5" />{s.likes}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div>
                <h4 className="text-sm font-semibold mb-2 flex items-center gap-1.5"><Send className="w-4 h-4" />Posts BookdHUB created about you</h4>
                {(!marketing?.posts || marketing.posts.length === 0) ? (
                  <p className="text-sm text-muted-foreground border border-dashed border-border rounded-sm p-6 text-center" data-testid="mkt-posts-empty">No posts yet. Reach a Top 50 or Top 100 milestone in a category and we'll post about you.</p>
                ) : (
                  <div className="space-y-2">
                    {marketing.posts.map((p, i) => (
                      <div key={i} className="bg-white border border-border rounded-sm p-3 flex gap-3" data-testid={`mkt-post-${i}`}>
                        {p.image_url && <img src={p.image_url} alt="" className="w-14 h-14 rounded-sm object-cover border border-border shrink-0" />}
                        <div className="min-w-0">
                          <div className="flex items-center gap-2">
                            <span className="text-[10px] uppercase tracking-wide font-bold text-primary">{p.source}</span>
                            <span className={`text-[10px] px-1.5 py-0.5 rounded-sm ${p.status === "Queued" ? "bg-green-100 text-green-800" : p.status === "Failed" ? "bg-red-100 text-red-700" : "bg-amber-100 text-amber-800"}`} data-testid={`mkt-post-status-${i}`}>{p.status}</span>
                          </div>
                          <p className="text-sm text-foreground mt-0.5 line-clamp-2">{p.text}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
